import React, { createContext, useContext, useState, useCallback } from 'react';
import { Photo } from '../types/photo';

interface PhotoContextType {
  photos: Photo[];
  setPhotos: React.Dispatch<React.SetStateAction<Photo[]>>;
  addPhotos: (newPhotos: Photo[]) => void;
  updatePhoto: (id: string, updates: Partial<Photo>) => void;
  removePhoto: (id: string) => void;
  toggleForWeb: (id: string) => void;
  toggleAllForWeb: (value: boolean) => void;
  updatePhotoProgress: (id: string, progress: number) => void;
  getPhotoUrls: () => string[];
}

const PhotoContext = createContext<PhotoContextType | undefined>(undefined);

interface PhotoProviderProps {
  children: React.ReactNode;
  initialPhotos?: string[];
}

export const PhotoProvider: React.FC<PhotoProviderProps> = ({ children, initialPhotos = [] }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);

  // Initialiser les photos existantes
  React.useEffect(() => {
    if (initialPhotos.length > 0) {
      console.log('🖼️ Initialisation des photos existantes dans PhotoProvider:', initialPhotos);
      const existingPhotos: Photo[] = initialPhotos.map((url, index) => ({
        id: `existing-${index}-${Date.now()}`,
        file: new File([], 'existing-photo.jpg'), // Fichier factice pour les photos existantes
        preview: url,
        serverUrl: url,
        progress: 100,
        forWeb: true
      }));
      setPhotos(existingPhotos);
    }
  }, [initialPhotos]);

  const addPhotos = useCallback((newPhotos: Photo[]) => {
    setPhotos(prev => [...prev, ...newPhotos]);
  }, []);

  const updatePhoto = useCallback((id: string, updates: Partial<Photo>) => {
    setPhotos(prev => prev.map(photo => 
      photo.id === id ? { ...photo, ...updates } : photo
    ));
  }, []);

  const removePhoto = useCallback((id: string) => {
    setPhotos(prev => prev.filter(photo => photo.id !== id));
  }, []);

  const toggleForWeb = useCallback((id: string) => {
    setPhotos(prev => prev.map(photo => 
      photo.id === id ? { ...photo, forWeb: !photo.forWeb } : photo
    ));
  }, []);

  const toggleAllForWeb = useCallback((value: boolean) => {
    setPhotos(prev => prev.map(photo => ({ ...photo, forWeb: value })));
  }, []);

  const updatePhotoProgress = useCallback((id: string, progress: number) => {
    setPhotos(prev => prev.map(photo =>
      photo.id === id ? { ...photo, progress } : photo
    ));
  }, []);

  // Fonction pour obtenir les URLs des photos pour la sauvegarde
  const getPhotoUrls = useCallback(() => {
    return photos
      .filter(photo => photo.forWeb && photo.progress === 100 && !photo.error)
      .map(photo => photo.serverUrl || photo.preview);
  }, [photos]);

  return (
    <PhotoContext.Provider value={{
      photos,
      setPhotos,
      addPhotos,
      updatePhoto,
      removePhoto,
      toggleForWeb,
      toggleAllForWeb,
      updatePhotoProgress,
      getPhotoUrls,
    }}>
      {children}
    </PhotoContext.Provider>
  );
};

export const usePhotoContext = () => {
  const context = useContext(PhotoContext);
  if (!context) {
    throw new Error('usePhotoContext must be used within a PhotoProvider');
  }
  return context;
};